// https://www.streamlinehq.com/icons/streamline-light/clothes-footwear-accessories/clothes-design/clothes-design-tape-measure

import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { MetricNature } from '@upright/common/model/input/types';
import { Valence } from '@upright/common/model/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { paiLensLayout } from '@upright/ui/common/upright/core/impact-lenses/pais/common/layout';
import { usePaiLensContext } from '@upright/ui/common/upright/core/impact-lenses/pais/common/use-pai-lens-context';
import styled from 'styled-components';
import { valenceToSide } from '../../../impact-lens-profile/common/utils';
import { useCarouselLayout } from '../../../impact-lens-profile/context';

import { getIconAndColorByNature } from '../common/icons';

interface Props {
  impact: ImpactCategoryId;
  index: number;
  valence: Valence;
  nature: MetricNature;
  count: number;
  isHighlighted: boolean;
  onClick?: (() => void | undefined) | undefined;
  className?: string;
}

export function PaiBox(props: Props) {
  const { impact, valence } = props;
  const baseLayout = useCarouselLayout();
  const lensContext = usePaiLensContext();
  const data = lensContext?.data.pais;
  if (!data) {
    return <></>;
  }
  const layout = paiLensLayout(baseLayout, data);
  const bounds = layout.impactPaiCountBoxArea(props.index, valenceToSide(valence), impact);

  const showNumber = bounds.width > 50;
  const symbolHeight = bounds.height - 4;
  const iconX = showNumber ? bounds.x1 + 8 : bounds.cx - symbolHeight / 2;
  const transform = `translate(${iconX},${bounds.y1 + (bounds.height - symbolHeight) / 2})`;

  const { Icon, color } = getIconAndColorByNature(props.nature);

  return (
    <G onClick={props.onClick} className={props.className}>
      <Rect
        rx={2}
        ry={2}
        x={bounds.x1}
        y={bounds.y1}
        height={bounds.height}
        width={bounds.width}
        isHighlighted={props.isHighlighted}
      />
      <g transform={transform}>
        <Icon style={{ stroke: color }} width={symbolHeight} height={symbolHeight} />
      </g>
      {showNumber && (
        <text
          fontWeight={400}
          fill={BouColor.BLACK}
          stroke="transparent"
          x={bounds.x2 - 15}
          y={bounds.y1 + bounds.height / 2}
          dy="0.35em"
          textAnchor="middle"
        >
          {props.count}
        </text>
      )}
    </G>
  );
}

const G = styled.g`
  cursor: pointer;
  transition: fill 0.12s ease-in-out;
  &:hover,
  &:active {
    fill: ${BouColor.TOOL_TEXT_LIGHT_GRAY};
  }
`;

const Rect = styled.rect<{ isHighlighted: boolean }>`
  fill: transparent;
  ${p =>
    p.isHighlighted
      ? `
  stroke: ${BouColor.BLACK};
  `
      : `
  stroke: ${BouColor.GREY_B3B3B3}
  `}
`;
